<template>
  <div ref="root" class="ticket-column-picker">
    <AppIconButton label="Columns" tooltip-placement="left" :aria-expanded="open ? 'true' : 'false'" aria-haspopup="dialog" @click="open = !open">
      <i class="pi pi-table" aria-hidden="true" />
    </AppIconButton>

    <div v-if="open" class="ticket-column-picker__popover" role="dialog" aria-label="Choose visible columns">
      <header>
        <div>
          <strong>Visible columns</strong>
          <small>Choose what appears in the table.</small>
        </div>
        <button type="button" aria-label="Close column settings" @click="open = false">×</button>
      </header>
      <div class="ticket-column-picker__options">
        <app-checkbox
          v-for="column in columns"
          :key="column.key"
          design="new"
          :value="isSelected(column.key)"
          :label="column.label"
          :readonly="locked.includes(column.key)"
          @change="toggle(column.key, $event)"
        />
      </div>
      <footer>
        <span>Ticket is always visible.</span>
        <button type="button" @click="showAll">Show all</button>
      </footer>
    </div>
  </div>
</template>

<script>
import AppIconButton from '@/components/AppIconButton.vue'

export default {
  name: 'SupportTicketColumnPicker',
  components: { AppIconButton },
  props: {
    value: { type: Array, required: true },
    columns: { type: Array, required: true },
    locked: { type: Array, default: () => ['ticket'] }
  },
  data() { return { open: false } },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    isSelected(key) { return this.value.includes(key) },
    toggle(key, selected) {
      if (this.locked.includes(key)) return
      const values = selected ? [...this.value, key] : this.value.filter(value => value !== key)
      this.$emit('input', this.columns.map(column => column.key).filter(columnKey => values.includes(columnKey)))
    },
    showAll() { this.$emit('input', this.columns.map(column => column.key)) },
    handleOutsideClick(event) {
      if (this.open && this.$refs.root && !this.$refs.root.contains(event.target)) this.open = false
    },
    handleKeydown(event) { if (event.key === 'Escape') this.open = false }
  }
}
</script>

<style lang="scss" scoped>
.ticket-column-picker { position: relative; z-index: 12; margin-left: auto; }
.ticket-column-picker__trigger { display: inline-flex; min-height: 36px; align-items: center; gap: $ui-space-8; padding: 0 $ui-space-12; border: 1px solid $ui-line; border-radius: 9px; background: $ui-surface; color: $ui-text; cursor: pointer; font: 600 $ui-font-size-metadata $ui-font-body; transition: border-color .15s ease, background-color .15s ease, box-shadow .15s ease; }
.ticket-column-picker__trigger:hover,
.ticket-column-picker__trigger:focus-visible,
.ticket-column-picker__trigger.is-open { border-color: $ui-blue; outline: 0; background: $ui-blue-soft; box-shadow: 0 0 0 3px rgba($ui-blue, .1); color: $ui-blue; }
.ticket-column-picker__trigger svg { width: 17px; height: 17px; fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 1.4; }
.ticket-column-picker__trigger strong { min-width: 34px; padding: 2px 7px; border-radius: 999px; background: $ui-surface-soft; color: $ui-muted; font-size: 11px; text-align: center; }
.ticket-column-picker__popover { position: absolute; z-index: 30; top: calc(100% + 8px); right: 0; width: 390px; overflow: hidden; border: 1px solid $ui-line-strong; border-radius: $ui-panel-radius; background: $ui-surface; box-shadow: 0 16px 40px rgba(23, 36, 58, .18); }
.ticket-column-picker__popover header { display: flex; align-items: flex-start; justify-content: space-between; gap: $ui-space-12; padding: $ui-space-16; border-bottom: 1px solid $ui-line; }
.ticket-column-picker__popover header div { display: grid; gap: 2px; }
.ticket-column-picker__popover header strong { color: $ui-ink; font: 600 $ui-font-size-control $ui-font-heading; }
.ticket-column-picker__popover header small { color: $ui-muted; font-size: $ui-font-size-metadata; }
.ticket-column-picker__popover header button { width: 28px; height: 28px; padding: 0; border: 0; border-radius: 7px; background: transparent; color: $ui-muted; cursor: pointer; font-size: 23px; line-height: 1; }
.ticket-column-picker__popover header button:hover,
.ticket-column-picker__popover header button:focus-visible { outline: 0; background: $ui-surface-soft; color: $ui-ink; }
.ticket-column-picker__options { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $ui-space-4 $ui-space-16; padding: $ui-space-12 $ui-space-16; }
.ticket-column-picker__options :deep(.checkbox) { min-width: 0; padding-bottom: 5px; }
.ticket-column-picker__options :deep(.checkbox__text) { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ticket-column-picker__popover footer { display: flex; min-height: 46px; align-items: center; justify-content: space-between; gap: $ui-space-12; padding: $ui-space-8 $ui-space-16; border-top: 1px solid $ui-line; background: $ui-surface-soft; }
.ticket-column-picker__popover footer span { color: $ui-muted; font-size: 11px; }
.ticket-column-picker__popover footer button { padding: 5px 0; border: 0; background: transparent; color: $ui-blue; cursor: pointer; font: 600 $ui-font-size-metadata $ui-font-body; }
@media (max-width: 560px) { .ticket-column-picker__popover { position: fixed; top: auto; right: $ui-space-12; bottom: $ui-space-12; left: $ui-space-12; width: auto; } }
</style>
